/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {Component, Prop, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import RevisionList from '@/components/RevisionList/index.vue';
import Loading from '@/components/Loading/index.vue';
import PageHeader from '@/components/PageHeader/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import {UgcItemInterface} from '@/store/modules/ugc-item/types';
import ProgressStatus from '@/components/ProgressStatus/index.vue';
import BuildThumbnail from '@/components/BuildThumbnail/scripts';
import {UgcBuildPresignedResponseInterface, UgcRevisionInterface} from '@/store/modules/ugc-build/types';
import ApiException from '@/exceptions/ApiException';

const namespace: string = 'ugcBuildModule';

/**
 * Item page view (component).
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'v-item',
    components: {
        'c-revision-list': RevisionList,
        'c-loading': Loading,
        'c-page-header': PageHeader,
        'c-progress-status': ProgressStatus,
        'c-build-thumbnail': BuildThumbnail,
    },
})
export default class Item extends Vue {
    @Action('loadRevisions', {namespace}) public loadRevisions: any;
    @Getter('revisions', {namespace}) public revisions!: UgcRevisionInterface[];
    @Action('presigned', {namespace}) public presigned: any;
    @Getter('item', {namespace}) public item!: UgcItemInterface;
    @Getter('hasRevisions', {namespace}) public hasRevisions!: boolean;
    @Getter('mostRecentItem', {namespace}) public mostRecentItem!: any;
    @Getter('lastRevision', {namespace}) public lastRevision!: UgcRevisionInterface | undefined;
    @Prop(String) public itemId!: string;

    public readonly MAX_REVISIONS: number = 25;
    public state: LoadingStateInterface = new LoadingState(true, false, 'home.items.loading', 'home.items.error');

    /**
     * Max items getter.
     *
     * @returns {boolean}
     */
    get revisionLimitExceeded(): boolean {
        return this.revisions.length >= this.MAX_REVISIONS;
    }

    /**
     * Created life-cycle event.
     */
    public created(): void {
        this.reloadItems();
    }

    /**
     * Artifact created checker.
     *
     * @returns {boolean}
     */
    get isComplete(): boolean {
        if (!this.lastRevision) {
            return false;
        }

        return 'complete' === this.lastRevision.status.toLowerCase();
    }

    /**
     * Reload the user items.
     *
     * @returns {Promise<void>}
     */
    public async reloadItems(): Promise<void> {
        try {
            await this.loadRevisions(this.item);
            this.state.complete();
        } catch (error) {
            this.state.failed();
        }
    }

    /**
     * Download the artifact.
     *
     * @param {UgcRevisionInterface} revision
     * @returns {Promise<void>}
     */
    public async downloadArtifact(): Promise<void> {
        try {
            const revision: UgcRevisionInterface = this.mostRecentItem('complete');

            if (!revision) {
                throw new Error('Could not download');
            }

            const presigned: UgcBuildPresignedResponseInterface = await this.presigned({
                build: revision,
                method: 'GET',
            });

            if (undefined === presigned) {
                throw new Error('File could not be found');
            }

            window.location.href = presigned.presigned_url;
        } catch (exception) {
            if (exception instanceof ApiException && 404 === exception.status) {
                this.reloadItems();
            }

            this.$toastr.error(this.$i18n.translate('notification.revision.download.failed', {}) || '');
        }
    }
}
